import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { IncidentsHeader } from './components/IncidentsHeader';
import { FilterBar } from './components/FilterBar';
import { IncidentsList } from './components/IncidentsList';
import { EmptyState } from './components/EmptyState';
import { useIncidents } from './hooks/useIncidents';
import { useIncidentFilters } from './hooks/useIncidentFilters';

export const IncidentsPage: React.FC = () => {
    const { incidents, stats, isLoading, isFetching, error, refresh } = useIncidents();
    const [isRefreshing, setIsRefreshing] = useState(false);
    
    const {
        filters,
        setFilterType,
        setSortType,
        setSearch,
        filteredIncidents,
        clearFilters
    } = useIncidentFilters(incidents);

    const handleRefresh = async () => {
        setIsRefreshing(true);
        await refresh();
        setTimeout(() => setIsRefreshing(false), 500);
    };

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center p-4">
                <div className="text-center">
                    <div className="w-16 h-16 rounded-2xl bg-[var(--status-down-bg)] text-[var(--status-down)] flex items-center justify-center mx-auto mb-4">
                        <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="12" y1="8" x2="12" y2="12"/>
                            <line x1="12" y1="16" x2="12.01" y2="16"/>
                        </svg>
                    </div>
                    <h2 className="text-[18px] font-semibold text-[var(--text)] mb-2">
                        Ошибка загрузки
                    </h2>
                    <p className="text-[13px] text-[var(--text-secondary)] mb-4">
                        {error.message}
                    </p>
                    <button
                        onClick={handleRefresh}
                        className="px-4 py-2 rounded-lg bg-[var(--brand)] text-white text-[13px] font-medium hover:opacity-90 transition-opacity"
                    >
                        Попробовать снова
                    </button>
                </div>
            </div>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="min-h-screen pb-20 md:pb-0"
        >
            <IncidentsHeader
                stats={stats}
                onRefresh={handleRefresh}
                isRefreshing={isRefreshing || isFetching}
                lastUpdate={stats.total > 0 ? incidents[0]?.updatedAt : null}
            />

            <FilterBar
                activeFilter={filters.type}
                activeSort={filters.sort}
                searchValue={filters.search}
                onFilterChange={setFilterType}
                onSortChange={setSortType}
                onSearchChange={setSearch}
                stats={stats}
            />

            {filteredIncidents.length === 0 && !isLoading ? (
                <EmptyState 
                    type={filters.type === 'all' && filters.search === '' ? 'all-resolved' : 'no-results'}
                    onClearFilters={filters.search || filters.type !== 'all' ? clearFilters : undefined}
                />
            ) : (
                <IncidentsList
                    incidents={filteredIncidents}
                    isLoading={isLoading}
                />
            )}

            <div className="px-[18px] mt-6 pt-4 border-t border-[var(--border)]">
                <div className="flex items-center justify-between text-[11px] text-[var(--text-muted)] font-mono">
                    <span>
                        {filteredIncidents.length} из {stats.total} инцидентов
                    </span>
                    <span>
                        Автообновление каждые 30 сек
                    </span>
                </div>
            </div>
        </motion.div>
    );
};